// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getApiBaseUrl } from "../services/apiBase";

const BASE = getApiBaseUrl();
const AuthContext = createContext(null);

// Custom event para sinalizar mudanças/recargas de condomínios entre páginas.
export const EVT_CONDOMINIOS_ATUALIZADOS = "condominios:atualizados";

export function AuthProvider({ children }) {
  const [usuario,          setUsuario]          = useState(null);
  const [condominioAtivo,  setCondominioAtivo]  = useState(null);
  const [condominiosDisponiveis, setCondominiosDisponiveis] = useState([]);
  const [modulosPlano,     setModulosPlano]     = useState(null);
  const [permissoes,       setPermissoes]       = useState({});
  const [carregando,       setCarregando]       = useState(true);
  const [avisoCondoSemAcesso, setAvisoCondoSemAcesso] = useState("");

  // Referência ao usuário corrente para evitar dependência cruzada em callbacks
  const usuarioRef = useRef(null);
  useEffect(() => { usuarioRef.current = usuario; }, [usuario]);

  // ── Requisição autenticada ──────────────────────────────────────────────
  const authFetch = useCallback(async (method, path, body = null) => {
    const token = localStorage.getItem("accessToken");
    const opts  = {
      method,
      headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(`${BASE}${path}`, opts);

    if (res.status === 401) {
      const json = await res.json();
      if (json.expired) {
        const ok = await tentarRefresh();
        if (ok) {
          const token2 = localStorage.getItem("accessToken");
          opts.headers["Authorization"] = `Bearer ${token2}`;
          const res2 = await fetch(`${BASE}${path}`, opts);
          const json2 = await res2.json();
          if (!res2.ok) { const e = new Error(json2.message || "Erro"); e.status = res2.status; throw e; }
          return json2;
        } else {
          logout();
          throw new Error("Sessão expirada.");
        }
      }
      const e = new Error(json.message || "Não autorizado"); e.status = 401; throw e;
    }

    const json = await res.json();
    if (!res.ok) { const e = new Error(json.message || "Erro"); e.status = res.status; throw e; }
    return json;
  }, []);

  // ── Refresh token ────────────────────────────────────────────────────────
  const tentarRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      localStorage.setItem("accessToken",  data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      return true;
    } catch { return false; }
  }, []);

  // ── Carrega lista de condomínios disponíveis ao usuário ──────────────────
  const recarregarCondominios = useCallback(async () => {
    const u = usuarioRef.current;
    if (!u) return [];

    // Síndico/Portaria/Porteiro/Condomino/Operacional: vínculos vêm do próprio /auth/me em u.condominios
    if (["Sindico", "Porteiro", "Portaria", "Condomino", "Operacional"].includes(u.perfil)) {
      try {
        const me = await fetch(`${BASE}/auth/me`, {
          headers: { "Authorization": `Bearer ${localStorage.getItem("accessToken")}` },
        });
        if (me.ok) {
          const d = await me.json();
          const lista = d?.usuario?.condominios || [];
          setCondominiosDisponiveis(lista);
          setUsuario(prev => prev ? { ...prev, condominios: lista } : prev);
          return lista;
        }
      } catch {}
      const lista = u.condominios || [];
      setCondominiosDisponiveis(lista);
      return lista;
    }

    // Admin / Gerente / SuperAdmin: usa /condominios (com tenant guard server-side)
    try {
      const res = await fetch(`${BASE}/condominios?limit=200`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (!res.ok) return [];
      const d = await res.json();
      const lista = d?.data || [];
      setCondominiosDisponiveis(lista);
      return lista;
    } catch { return []; }
  }, []);

  // ── Login ────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, senha, lembrar = false) => {
    const res = await fetch(`${BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, senha }),
    });
    let data = {};
    try { data = await res.json(); } catch {}
    if (!res.ok) {
      const err = new Error(data.message || "Erro ao fazer login.");
      err.status = res.status;
      err.code = data.code;
      err.retryAfter = Number(res.headers.get("Retry-After")) || data.retryAfter || 0;
      throw err;
    }

    localStorage.setItem("accessToken",  data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    if (lembrar) localStorage.setItem("lembrar", "1");

    setUsuario(data.usuario);
    usuarioRef.current = data.usuario;

    try {
      const pRes = await fetch(`${BASE}/permissoes/minhas`, {
        headers: { "Authorization": `Bearer ${data.accessToken}` },
      });
      if (pRes.ok) {
        const pData = await pRes.json();
        setPermissoes(pData.data?.permissoes || {});
        localStorage.setItem("permissoes", JSON.stringify(pData.data?.permissoes || {}));
      }
    } catch {}

    // Síndico/Portaria/Porteiro: auto-seleciona o condomínio vinculado
    const condosSindico = data.usuario.condominios || [];
    if (["Sindico", "Portaria", "Porteiro", "Operacional"].includes(data.usuario.perfil) && condosSindico.length > 0) {
      const condo = condosSindico[0];
      localStorage.setItem("condominioAtivo", String(condo.id));
      setCondominioAtivo(condo);
      setCondominiosDisponiveis(condosSindico);
    } else {
      localStorage.removeItem("condominioAtivo");
      setCondominioAtivo(null);

      // Carrega lista de condomínios via API
      try {
        const r = await fetch(`${BASE}/condominios?limit=200`, {
          headers: { "Authorization": `Bearer ${data.accessToken}` },
        });
        if (r.ok) {
          const dl = await r.json();
          setCondominiosDisponiveis(dl?.data || []);
        }
      } catch {}
    }

    // Carrega módulos do plano do tenant
    const u = data.usuario;
    if (u.isSuperAdmin || !u.idCliente) {
      setModulosPlano(null);
    } else if (u.idCliente) {
      try {
        const modRes = await fetch(`${BASE}/planos/modulos-tenant`, {
          headers: { "Authorization": `Bearer ${data.accessToken}` },
        });
        if (modRes.ok) {
          const modData = await modRes.json();
          setModulosPlano(modData.modulos || null);
        }
      } catch { setModulosPlano(null); }
    }

    if (u.clienteStatus === "Bloqueado") {
      throw new Error("TENANT_BLOCKED: Acesso bloqueado por inadimplência. Entre em contato com o suporte.");
    }
    if (u.clienteStatus === "Cancelado") {
      throw new Error("TENANT_CANCELLED: Esta conta foi cancelada. Entre em contato com o suporte.");
    }

    return data.usuario;
  }, []);

  // ── Logout ───────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (token) {
        await fetch(`${BASE}/auth/logout`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        });
      }
    } catch {}
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("permissoes");
    localStorage.removeItem("condominioAtivo");
    setUsuario(null);
    setCondominioAtivo(null);
    setCondominiosDisponiveis([]);
    setPermissoes({});
  }, []);

  // ── Troca condomínio ativo ────────────────────────────────────────────────
  const trocarCondominio = useCallback((condo) => {
    setCondominioAtivo(condo);
    if (condo) localStorage.setItem("condominioAtivo", String(condo.id));
    else localStorage.removeItem("condominioAtivo");
  }, []);

  // ── Verifica sessão ao carregar ───────────────────────────────────────────
  useEffect(() => {
    const verificar = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) { setCarregando(false); return; }
      try {
        let res = await fetch(`${BASE}/auth/me`, { headers: { "Authorization": `Bearer ${token}` } });

        // Tenta refresh se 401
        if (!res.ok) {
          const ok = await tentarRefresh();
          if (ok) {
            const token2 = localStorage.getItem("accessToken");
            res = await fetch(`${BASE}/auth/me`, { headers: { "Authorization": `Bearer ${token2}` } });
          }
        }

        if (res.ok) {
          const data = await res.json();
          setUsuario(data.usuario);
          usuarioRef.current = data.usuario;

          const savedPerm = localStorage.getItem("permissoes");
          if (savedPerm) try { setPermissoes(JSON.parse(savedPerm)); } catch {}

          try {
            const pRes = await fetch(`${BASE}/permissoes/minhas`, {
              headers: { "Authorization": `Bearer ${localStorage.getItem("accessToken")}` },
            });
            if (pRes.ok) {
              const pData = await pRes.json();
              setPermissoes(pData.data?.permissoes || {});
              localStorage.setItem("permissoes", JSON.stringify(pData.data?.permissoes || {}));
            }
          } catch {}

          // Carrega lista de condomínios disponíveis
          let lista = [];
          if (["Sindico", "Porteiro", "Portaria", "Condomino", "Operacional"].includes(data.usuario.perfil)) {
            lista = data.usuario.condominios || [];
          } else {
            try {
              const r = await fetch(`${BASE}/condominios?limit=200`, {
                headers: { "Authorization": `Bearer ${localStorage.getItem("accessToken")}` },
              });
              if (r.ok) {
                const dl = await r.json();
                lista = dl?.data || [];
              }
            } catch {}
          }
          setCondominiosDisponiveis(lista);

          // Restaura condomínio salvo se existir e ainda for válido
          const savedCondo = localStorage.getItem("condominioAtivo");
          const condo = savedCondo ? lista.find(c => String(c.id) === savedCondo) : null;
          if (savedCondo && !condo) {
            // Usuário perdeu acesso ao condomínio que estava salvo
            setAvisoCondoSemAcesso("Você não tem mais acesso ao condomínio salvo anteriormente. Selecione um novo.");
            localStorage.removeItem("condominioAtivo");
          }
          setCondominioAtivo(condo);

          // Recarrega módulos do plano
          if (data.usuario.isSuperAdmin || !data.usuario.idCliente) {
            setModulosPlano(null);
          } else {
            try {
              const modRes = await fetch(`${BASE}/planos/modulos-tenant`, {
                headers: { "Authorization": `Bearer ${localStorage.getItem("accessToken")}` },
              });
              if (modRes.ok) {
                const modData = await modRes.json();
                setModulosPlano(modData.modulos || null);
              }
            } catch { setModulosPlano(null); }
          }
        } else {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
        }
      } catch {}
      setCarregando(false);
    };
    verificar();
  }, [tentarRefresh]);

  // ── Sincronização entre abas via 'storage' event ─────────────────────────
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "condominioAtivo") {
        const novo = e.newValue;
        if (!novo) { setCondominioAtivo(null); return; }
        const c = condominiosDisponiveis.find(cc => String(cc.id) === novo);
        if (c) setCondominioAtivo(c);
      }
      if (e.key === "accessToken" && !e.newValue) {
        // Logout em outra aba
        setUsuario(null);
        setCondominioAtivo(null);
        setCondominiosDisponiveis([]);
        setPermissoes({});
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [condominiosDisponiveis]);

  // ── Listener global p/ recarregar lista após criar/editar/excluir condomínio
  useEffect(() => {
    const handler = () => { recarregarCondominios(); };
    window.addEventListener(EVT_CONDOMINIOS_ATUALIZADOS, handler);
    return () => window.removeEventListener(EVT_CONDOMINIOS_ATUALIZADOS, handler);
  }, [recarregarCondominios]);

  const value = {
    usuario,
    condominioAtivo,
    condominiosDisponiveis,
    carregando,
    modulosPlano,
    avisoCondoSemAcesso,
    setAvisoCondoSemAcesso,
    login, logout, trocarCondominio, authFetch,
    recarregarCondominios,
    permissoes,
    temAcesso: (modulo) => {
      if (!usuario) return false;
      if (usuario.perfil === "Admin") return true;
      return (permissoes[modulo] || "Total") !== "Oculto";
    },
    podeEditar: (modulo) => {
      if (!usuario) return false;
      if (usuario.perfil === "Admin") return true;
      return (permissoes[modulo] || "Total") === "Total";
    },
    isAdmin:   usuario?.perfil === "Admin",
    isGerente: usuario?.perfil === "Gerente",
    isSindico: usuario?.perfil === "Sindico",
    isCondomino: usuario?.perfil === "Condomino",
    idCondoAtivo: condominioAtivo?.id || null,
    isSuperAdmin: !!usuario?.isSuperAdmin && !usuario?.idCliente,
    temModulo: (mod) => {
      if (!modulosPlano) return true;
      const modLower = (mod || "").toLowerCase();
      return modulosPlano.some(m => m.toLowerCase() === modLower);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}
